import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney } from '../lib/format.js'
import { uploadReceivePhoto } from '../lib/photoUpload.js'
import { resolveActiveRestaurantId } from '../lib/activeRestaurant.js'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'
import KitchenLoader from './KitchenLoader.jsx'

// Modal for AI-assisted invoice scanning (Path B).
//
// Flow:
//   1. PICK     — manager picks/snaps a photo of an invoice
//   2. UPLOAD   — photo goes to Supabase Storage (receive-photos)
//   3. EXTRACT  — call edge function `extract-invoice`, which calls
//                 Claude Vision and returns structured data
//   4. REVIEW   — show extracted data; manager edits anything wrong
//                 and approves/skips individual line items
//   5. SUBMIT   — bulk-insert receive_logs (one per approved line)
//
// Items are matched against the existing catalog by case-insensitive
// name. Matched items log normally; unmatched items are skipped with
// a clear count so the manager knows what's left to do manually.
// All inserted receive_logs share the same photo_url so the original
// invoice is one tap away from each row.
export default function InvoiceScanModal({
  open,
  onClose,
  onLogged,        // (count) → void
  items,           // catalog for name matching
  suppliers,       // catalog for supplier matching
}) {
  const fileRef = useRef(null)
  const { currentRestaurantId, isGrouped, loading } = useCurrentUnit()
  const [phase, setPhase] = useState('pick')
  // 'pick' | 'uploading' | 'extracting' | 'review' | 'submitting' | 'error'
  const [filename, setFilename] = useState('')
  const [photoPreview, setPhotoPreview] = useState(null)  // local object URL
  const [photoPath, setPhotoPath] = useState(null)        // private Storage object path
  // Destination unit resolved ONCE at photo-pick time. Reused at submit so
  // the photo, the new catalog items, and the receive_logs rows all land on
  // the SAME unit (the operator's selected unit when the photo was taken).
  const [resolvedRestaurantId, setResolvedRestaurantId] = useState(null)
  const [extracted, setExtracted] = useState(null)
  const [error, setError] = useState(null)

  // Editable form fields populated from extraction
  const [supplierName, setSupplierName] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [lineItems, setLineItems] = useState([])
  // each: { id, description, quantity, unit, cost_per_unit, line_total,
  //         item_id (or null), item_name (display), approved (bool) }

  // Reset on every open so a new scan starts clean.
  useEffect(() => {
    if (open) {
      setPhase('pick')
      setFilename('')
      setPhotoPath(null)
      setResolvedRestaurantId(null)
      setExtracted(null)
      setError(null)
      setSupplierName('')
      setInvoiceDate('')
      setLineItems([])
      if (photoPreview) URL.revokeObjectURL(photoPreview)
      setPhotoPreview(null)
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup local object URL on unmount.
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview)
    }
  }, [photoPreview])

  // Build a lowercase name → item lookup so matching is O(1).
  const itemsByName = useMemo(() => {
    const m = new Map()
    for (const i of items ?? []) {
      m.set((i.name || '').toLowerCase().trim(), i)
    }
    return m
  }, [items])

  // Resolve supplier the same way ReceiveEntryForm does — match the
  // extracted name to an existing supplier (case-insensitive) or
  // accept it as free-text and store on supplier_name only.
  const resolvedSupplier = useMemo(() => {
    const trimmed = supplierName.trim()
    if (!trimmed) return null
    const exact = (suppliers ?? []).find(
      (s) => s.name.toLowerCase() === trimmed.toLowerCase()
    )
    if (exact) return { id: exact.id, name: exact.name }
    return { id: null, name: trimmed }
  }, [supplierName, suppliers])

  if (!open) return null

  // ---------- Phase transitions ----------------------------

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type?.startsWith('image/')) {
      setError('Please pick an image file.')
      return
    }
    if (file.size > 12 * 1024 * 1024) {
      setError('That photo is over 12 MB — try a smaller one.')
      return
    }
    setFilename(file.name)
    setError(null)

    // Block while the unit context is still loading. Before load, isGrouped
    // can transiently read false and the resolver would guess the first
    // active unit — the exact mis-attribution bug. Stay on 'pick' and prompt
    // a retry; no upload, no insert.
    if (loading) {
      setError('One moment — still loading your units.')
      setPhase('pick')
      return
    }

    // Resolve the destination unit BEFORE uploading. For a grouped operator
    // on the all-units roll-up this is null — we must not guess a unit, so
    // block here (no upload, no insert) and prompt them to pick one. We stay
    // on the pick phase so the operator can retry after selecting a unit.
    const restaurantId = await resolveActiveRestaurantId({ currentRestaurantId, isGrouped })
    if (!restaurantId) {
      setError(
        isGrouped
          ? 'Select a unit before scanning an invoice.'
          : 'We could not find your restaurant. Try reopening this screen.'
      )
      setPhase('pick')
      return
    }
    setResolvedRestaurantId(restaurantId)

    if (photoPreview) URL.revokeObjectURL(photoPreview)
    setPhotoPreview(URL.createObjectURL(file))

    // Upload, then extract — one continuous flow with phase transitions
    setPhase('uploading')
    let path
    try {
      // uploadReceivePhoto throws a friendly error if this is missing.
      path = await uploadReceivePhoto(file, restaurantId)
      setPhotoPath(path)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InvoiceScanModal] upload failed', err)
      setError('Could not upload the photo. Try again.')
      setPhase('error')
      return
    }

    setPhase('extracting')
    try {
      // Use direct fetch instead of supabase.functions.invoke so the
      // actual HTTP error (status + body) is visible. The SDK wraps
      // errors in a way that hides the real cause. The auth + apikey
      // headers come from the same env as the supabase client.
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sessData } = await supabase.auth.getSession()
      const token = sessData?.session?.access_token || anonKey

      const fnUrl = `${supabaseUrl}/functions/v1/extract-invoice`

      const resp = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'authorization': `Bearer ${token}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({ photo_path: path }),
      })

      if (!resp.ok) {
        throw new Error(`extract-invoice HTTP ${resp.status}`)
      }

      const data = await resp.json()
      if (!data) throw new Error('Empty response from extract-invoice')
      if (data.error === 'image_unreadable') {
        setError("Couldn't read this invoice. Try a clearer, well-lit photo.")
        setPhase('error')
        return
      }
      if (data.error) {
        setError(data.message || 'Extraction failed. Try again.')
        setPhase('error')
        return
      }
      setExtracted(data)
      hydrateForm(data)
      setPhase('review')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InvoiceScanModal] extraction failed', err)
      setError("Couldn't process this invoice. Try a clearer photo or check your connection.")
      setPhase('error')
    }
  }

  function hydrateForm(data) {
    setSupplierName(data.supplier_name ?? '')
    setInvoiceDate(data.invoice_date ?? '')
    const lines = (data.line_items ?? []).map((li, idx) => {
      const desc = (li.description || '').trim()
      const matched = itemsByName.get(desc.toLowerCase())
      return {
        id:            `${idx}`,
        description:   desc,
        quantity:      Number(li.quantity)      || 0,
        unit:          matched?.unit || li.unit || 'each',
        cost_per_unit: Number(li.cost_per_unit) || 0,
        line_total:    Number(li.line_total)    || 0,
        item_id:       matched?.id || null,
        item_name:     matched?.name || desc,
        // Default approved=true for ALL lines — for unmatched lines,
        // approving creates the item in the catalog before logging.
        // The whole point of the scan is to seed the catalog.
        approved:      true,
      }
    })
    setLineItems(lines)
  }

  function updateLine(id, patch) {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row
        const merged = { ...row, ...patch }
        // If description changed, re-run the match. Don't auto-toggle
        // approved — the user already decided on that toggle. If they
        // edited the description to match an existing item, the badge
        // updates but their approve choice persists.
        if ('description' in patch) {
          const m = itemsByName.get((patch.description || '').toLowerCase().trim())
          merged.item_id = m?.id || null
          merged.item_name = m?.name || merged.description
          if (m) merged.unit = m.unit
        }
        // Recompute line_total when qty or cost change
        if ('quantity' in patch || 'cost_per_unit' in patch) {
          merged.line_total = Number((Number(merged.quantity) * Number(merged.cost_per_unit)).toFixed(2))
        }
        return merged
      })
    )
  }

  function removeLine(id) {
    setLineItems((prev) => prev.filter((r) => r.id !== id))
  }

  async function handleSubmit() {
    if (phase === 'submitting') return
    if (!resolvedSupplier) {
      setError('Supplier name is required.')
      return
    }
    const approvedRows = lineItems.filter((r) => r.approved)
    if (approvedRows.length === 0) {
      setError('Approve at least one line.')
      return
    }

    // Reuse the SAME unit resolved at photo-pick time — do NOT re-resolve.
    // This keeps the new catalog items and the receive_logs rows on the
    // same unit as the uploaded photo. If it is somehow missing, stop
    // rather than letting the trigger guess a unit.
    const restaurantId = resolvedRestaurantId
    if (!restaurantId) {
      setError(
        isGrouped
          ? 'Select a unit before scanning an invoice.'
          : 'We could not find your restaurant. Start the scan again.'
      )
      return
    }

    setPhase('submitting')
    setError(null)

    try {
      // ---------- Step 1: create catalog items for approved unmatched lines ----------
      // The scan acts as catalog-seeding: anything approved but not in
      // the catalog gets inserted into items with the data the AI
      // pulled (name, unit, cost). Manager can refine pars and category
      // later in Settings. restaurant_id is set EXPLICITLY (to the same
      // unit as the photo) so the single-tenant trigger no-ops instead of
      // guessing an arbitrary membership for multi-unit operators.
      const linesNeedingItems = approvedRows.filter((r) => !r.item_id)
      const newItemIdByDesc = new Map()
      if (linesNeedingItems.length > 0) {
        // Dedupe by description in case the invoice has the same item
        // on multiple lines — only create one items row per name.
        const dedup = new Map()
        for (const r of linesNeedingItems) {
          const key = r.description.toLowerCase().trim()
          if (!dedup.has(key)) {
            dedup.set(key, {
              restaurant_id:  restaurantId,
              name:           r.description.trim(),
              unit:           r.unit || 'each',
              cost_per_unit:  Number(r.cost_per_unit) || 0,
              par_qty:        0,
              current_on_hand: 0,
              active:         true,
            })
          }
        }
        const itemInserts = [...dedup.values()]
        const { data: createdItems, error: itemsErr } = await supabase
          .from('items')
          .insert(itemInserts)
          .select('id, name')
        if (itemsErr) throw itemsErr
        for (const it of createdItems ?? []) {
          newItemIdByDesc.set((it.name || '').toLowerCase().trim(), it.id)
        }
      }

      // ---------- Step 2: insert receive_logs for every approved line ----------
      // Newly-created items get matched up by lowercased description.
      const payload = approvedRows.map((r) => {
        const itemId = r.item_id
          ?? newItemIdByDesc.get(r.description.toLowerCase().trim())
        const total = Number((Number(r.quantity) * Number(r.cost_per_unit)).toFixed(2))
        return {
          // Explicit restaurant_id (same unit as the photo) so the
          // single-tenant trigger no-ops instead of mis-filing the row.
          restaurant_id:         restaurantId,
          supplier_id:           resolvedSupplier.id,
          supplier_name:         resolvedSupplier.name,
          item_id:               itemId,
          item_name:             r.description.trim(),
          quantity:              Number(r.quantity),
          unit:                  r.unit,
          cost_per_unit_at_log:  Number(Number(r.cost_per_unit).toFixed(4)),
          total_cost:            total,
          notes:                 invoiceDate ? `Invoice ${invoiceDate}` : null,
          photo_url:             photoPath,
        }
      })

      const { error: insErr } = await supabase.from('receive_logs').insert(payload)
      if (insErr) throw insErr

      onLogged?.({
        deliveries: approvedRows.length,
        newItems:   linesNeedingItems.length,
      })
      onClose?.()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InvoiceScanModal] submit failed', err)
      // Deterministic friendly message only — never interpolate err.message
      // into a customer-facing string. RLS denial (Postgres 42501) means the
      // operator lacks access to the resolved unit, which is worth calling out.
      const isRls = err?.code === '42501' || /row-level security/i.test(err?.message || '')
      setError(
        isRls
          ? 'This delivery couldn’t be saved to the selected unit — check that you have access to it.'
          : 'Couldn’t save this delivery. Try again.'
      )
      setPhase('review')
    }
  }

  // ---------- Render ---------------------------------------

  // Quick stats for the review header. Approved counts include
  // unmatched lines now — they'll get items auto-created on submit.
  const matchedCount   = lineItems.filter((r) => r.item_id).length
  const approvedCount  = lineItems.filter((r) => r.approved).length
  const unmatchedCount = lineItems.filter((r) => !r.item_id).length
  const newItemsCount  = lineItems.filter((r) => r.approved && !r.item_id).length
  const approvedTotal  = lineItems.reduce(
    (sum, r) => sum + (r.approved ? Number(r.quantity) * Number(r.cost_per_unit) : 0),
    0
  )

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Scan invoice"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Scan invoice</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* ---------- Phase: PICK ---------- */}
          {phase === 'pick' && (
            <>
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
                Snap a clear photo of a supplier invoice or delivery receipt.
                AI extracts the supplier, date, and line items so you don't
                have to type them. You'll review and edit before saving.
              </div>

              {/* Hidden file input + custom-styled button. The default
                  browser "Choose File" UI is generic and ugly; this gives
                  us a clear "Take photo" / "Pick from gallery" CTA. The
                  capture="environment" attribute opens the back camera
                  on phones; on desktop it falls back to the file picker. */}
              <input
                ref={fileRef}
                id="invoice-photo"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFile}
                className="sr-only"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-lg font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800"
              >
                Take photo of invoice
              </button>
              <p className="text-center text-xs text-black/55">
                Opens your camera. Tips: lay the invoice flat, fill the
                frame, avoid glare, hold steady.
              </p>
            </>
          )}

          {/* ---------- Phase: UPLOAD / EXTRACT (loading) ---------- */}
          {(phase === 'uploading' || phase === 'extracting') && (
            <div className="flex flex-col items-center gap-4 py-8">
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt="Invoice"
                  className="max-h-48 rounded-lg border border-black/10 bg-black/5 object-contain"
                />
              )}
              {phase === 'uploading' ? (
                <KitchenLoader
                  align="row"
                  size="sm"
                  messages={['Uploading photo\u2026']}
                />
              ) : (
                <KitchenLoader
                  messages={[
                    'Squinting at the invoice\u2026',
                    'Picking out line items\u2026',
                    'Matching to your catalog\u2026',
                    'Plating the receive log\u2026',
                  ]}
                />
              )}
            </div>
          )}

          {/* ---------- Phase: REVIEW ---------- */}
          {phase === 'review' && (
            <>
              {photoPreview && (
                <a
                  href={photoPreview}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  title="View full-size photo"
                >
                  <img
                    src={photoPreview}
                    alt="Invoice"
                    className="block max-h-40 w-full rounded-lg border border-black/10 bg-black/5 object-contain"
                  />
                </a>
              )}

              {/* Header: supplier + date */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium" htmlFor="inv-supplier">
                    Supplier
                  </label>
                  <input
                    id="inv-supplier"
                    type="text"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Supplier name"
                    className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                  />
                  {supplierName && resolvedSupplier && !resolvedSupplier.id && (
                    <p className="mt-1 text-xs text-black/55">
                      Will log as new supplier (not in your catalog yet).
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium" htmlFor="inv-date">
                    Invoice date
                  </label>
                  <input
                    id="inv-date"
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                  />
                </div>
              </div>

              {/* Match summary */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 text-sm text-emerald-900">
                <span className="font-medium tabular-nums">{matchedCount}</span> of{' '}
                <span className="font-medium tabular-nums">{lineItems.length}</span>{' '}
                {lineItems.length === 1 ? 'line' : 'lines'} already in your catalog.
                {unmatchedCount > 0 && (
                  <>
                    {' '}The other{' '}
                    <span className="font-medium tabular-nums">{unmatchedCount}</span>{' '}
                    will be added as new items when you log
                    {' '}— uncheck any line you'd rather skip.
                  </>
                )}
              </div>

              {/* Line items */}
              <div className="space-y-3">
                {lineItems.map((row) => (
                  <LineItemEditor
                    key={row.id}
                    row={row}
                    onChange={(patch) => updateLine(row.id, patch)}
                    onRemove={() => removeLine(row.id)}
                  />
                ))}
                {lineItems.length === 0 && (
                  <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
                    No line items extracted from this image.
                  </div>
                )}
              </div>

              {/* Approved total */}
              {approvedCount > 0 && (
                <div className="flex items-center justify-between rounded-lg bg-black/5 px-3 py-2">
                  <span className="text-sm text-black/70">Total approved</span>
                  <span className="text-xl font-medium tabular-nums">
                    {formatMoney(approvedTotal)}
                  </span>
                </div>
              )}
            </>
          )}

          {/* ---------- Phase: SUBMITTING ---------- */}
          {phase === 'submitting' && (
            <div className="flex flex-col items-center gap-2 py-8 text-sm font-medium text-black/75">
              <svg className="h-5 w-5 animate-spin text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
              Saving deliveries…
            </div>
          )}

          {/* ---------- Phase: ERROR (with retry) ---------- */}
          {phase === 'error' && (
            <div className="space-y-3">
              <p className="text-sm text-black/70">Something went wrong. Try a different photo or check your connection.</p>
              <button
                type="button"
                onClick={() => setPhase('pick')}
                className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {phase === 'review' && (
          <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={approvedCount === 0 || !resolvedSupplier}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {approvedCount === 0
                ? 'Approve at least one line'
                : `Log ${approvedCount} ${approvedCount === 1 ? 'delivery' : 'deliveries'}${
                    newItemsCount > 0
                      ? ` (+${newItemsCount} new ${newItemsCount === 1 ? 'item' : 'items'})`
                      : ''
                  } \u00b7 ${formatMoney(approvedTotal)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------- Line item row editor ---------------------------

function LineItemEditor({ row, onChange, onRemove }) {
  const isMatched = !!row.item_id
  return (
    <div className={`rounded-xl border p-3 ${
      isMatched ? 'border-black/15 bg-white' : 'border-amber-300 bg-amber-50/40'
    }`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        {isMatched ? (
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
            Matched
          </span>
        ) : (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            New item
          </span>
        )}
        <div className="flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium">
            <input
              type="checkbox"
              checked={row.approved}
              onChange={(e) => onChange({ approved: e.target.checked })}
              className="h-4 w-4"
            />
            {isMatched ? 'Log this line' : 'Add + log'}
          </label>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove this line"
            className="min-h-[36px] min-w-[36px] rounded-lg px-2 text-black/40 hover:bg-red-50 hover:text-red-600"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <input
          type="text"
          value={row.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Item name"
          className="w-full rounded-lg border border-black/15 px-3 py-2 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
        />
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] font-medium text-black/55">Qty</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={row.quantity}
              onChange={(e) => onChange({ quantity: e.target.value })}
              className="w-full rounded-lg border border-black/15 px-2 py-2 text-base font-medium tabular-nums outline-none focus:border-blue-600"
            />
          </div>
          <div>
            <label className="text-[10px] font-medium text-black/55">Unit</label>
            <select
              value={row.unit}
              onChange={(e) => onChange({ unit: e.target.value })}
              className="w-full rounded-lg border border-black/15 bg-white px-2 py-2 text-base outline-none focus:border-blue-600"
            >
              <option value="lb">lb</option>
              <option value="oz">oz</option>
              <option value="each">each</option>
              <option value="case">case</option>
              <option value="gallon">gallon</option>
              <option value="count">count</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-medium text-black/55">Cost / unit</label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={row.cost_per_unit}
              onChange={(e) => onChange({ cost_per_unit: e.target.value })}
              className="w-full rounded-lg border border-black/15 px-2 py-2 text-base font-medium tabular-nums outline-none focus:border-blue-600"
            />
          </div>
        </div>
        <div className="text-right text-xs text-black/55">
          Line total: <span className="font-medium tabular-nums">
            {formatMoney(Number(row.quantity) * Number(row.cost_per_unit))}
          </span>
        </div>
      </div>
    </div>
  )
}
